// The words each box in the report pane is read out by (TGCLOUD-1868, plan D1): "Variant, as read
// from the report". Pure: no React.
//
// Every box is a button, and a screen reader names a button by its label, so a box needs words for
// the field it marks, never its path. The caller may name the field itself (`anchor.label`: the
// verification view knows the gene and the c. it shows beside the box). Otherwise the field is
// named from its path in the proposal (backend services/testResults/proposal.js): `variants.0.variantC`,
// `genesTested.2.coverage.status`. A path this list does not know is still named, plainly.
//
// In the caller's `t` (TGCLOUD-1893), English by default: the names below are the English keys, and
// each label is one sentence with the name and the number as placeholders.
import { english } from 'src/i18n/language';

const TOP_LEVEL = Object.freeze({
  lab: 'Laboratory',
  dateResultReceived: 'Date result received',
  testName: 'Test name',
  testCode: 'Test code',
  resultSummaryAsPrinted: 'Result summary',
  testResultSuggestion: 'Test result',
  testResult: 'Test result',
  testScope: 'Test scope',
  genesTestedList: 'Gene list'
});

// [the rest of the path, its name], most specific first. A key matches the rest of the path whole
// or up to a dot, so `zygosity` names `zygosity.value` and `zygosity.asPrinted`.
const VARIANT_FIELDS = Object.freeze([
  ['variantC', 'Variant'],
  ['hgvsAsPrinted.c', 'Variant'],
  ['variantP', 'Protein change'],
  ['hgvsAsPrinted.p', 'Protein change'],
  ['variantG', 'Genomic change'],
  ['hgvsAsPrinted.g', 'Genomic change'],
  ['gene', 'Gene'],
  ['transcript', 'Transcript'],
  ['genomeBuild', 'Genome build'],
  ['zygosity', 'Zygosity'],
  ['inheritance', 'Inheritance'],
  ['origin', 'Origin'],
  ['variantInterpretation', 'Variant interpretation'],
  ['labClassification.frameworkAsPrinted', 'Classification framework'],
  ['labClassification.classificationDateAsPrinted', 'Classification date'],
  ['labClassification.classificationDate', 'Classification date'],
  ['labClassification.classifyingLabAsPrinted', 'Classifying laboratory'],
  ['labClassification.criteria', 'Classification criterion'],
  ['labClassification', 'Laboratory’s classification']
]);
const VARIANT_ITEM = 'Variant';

const GENE_FIELDS = Object.freeze([
  ['gene', 'Gene tested'],
  ['scope', 'Scope'],
  ['regionDescriptionAsPrinted', 'Region studied'],
  ['transcriptAsPrinted', 'Transcript'],
  ['transcript', 'Transcript'],
  ['methods', 'Method'],
  ['coverage', 'Coverage']
]);
const GENE_ITEM = 'Gene tested';

const matches = (rest, key) => rest === key || rest.startsWith(`${key}.`);
const nameIn = (fields, rest) => fields.find(([key]) => matches(rest, key));

const LIST_ITEM =
  /^(variants|genesTested|reportKeys|notRead|individuals)\.(\d+)(?:\.(.+))?$/;

/**
 * How many distinct variants, genes and report references the anchors mark, so that a name says
 * which one it is only when there is more than one ("Protein change, variant 2").
 */
export function countsOf(anchors) {
  const seen = {
    variants: new Set(),
    genesTested: new Set(),
    reportKeys: new Set()
  };
  for (const anchor of Array.isArray(anchors) ? anchors : []) {
    const m = LIST_ITEM.exec((anchor && anchor.path) || '');
    if (m && seen[m[1]]) seen[m[1]].add(m[2]);
  }
  return {
    variants: seen.variants.size,
    genesTested: seen.genesTested.size,
    reportKeys: seen.reportKeys.size
  };
}

// An item's own field is numbered as the item ("Variant 2"); any other field says which item it
// belongs to ("Coverage, gene 2"). `name` is the English key; it comes back in `t`.
const numbered = (name, itemName, noun, index, count, t) => {
  if (!(count > 1)) return t(name);
  const number = index + 1;
  if (name === itemName) {
    return noun === 'variant'
      ? t('Variant {{number}}', { number })
      : noun === 'gene'
      ? t('Gene tested {{number}}', { number })
      : t('Report reference {{number}}', { number });
  }
  const field = t(name);
  return noun === 'variant'
    ? t('{{field}}, variant {{number}}', { field, number })
    : noun === 'gene'
    ? t('{{field}}, gene {{number}}', { field, number })
    : t('{{field}}, reference {{number}}', { field, number });
};

/**
 * The field a path marks, in words, and whether it is a value read from the report.
 *
 * @returns {{ name: string, isValue: boolean }}  `name` in `t`
 */
export function fieldOf(path, counts = {}, t = english) {
  const text = typeof path === 'string' ? path : '';
  const list = LIST_ITEM.exec(text);
  if (list) {
    const [, kind, at, rest = ''] = list;
    const index = Number(at);
    if (kind === 'variants') {
      const found = nameIn(VARIANT_FIELDS, rest);
      return {
        name: numbered(
          found ? found[1] : rest ? 'Variant detail' : VARIANT_ITEM,
          VARIANT_ITEM,
          'variant',
          index,
          counts.variants,
          t
        ),
        isValue: true
      };
    }
    if (kind === 'genesTested') {
      const found = nameIn(GENE_FIELDS, rest);
      return {
        name: numbered(
          found ? found[1] : rest ? 'Gene tested detail' : GENE_ITEM,
          GENE_ITEM,
          'gene',
          index,
          counts.genesTested,
          t
        ),
        isValue: true
      };
    }
    if (kind === 'reportKeys') {
      return {
        name: numbered(
          'Report reference',
          'Report reference',
          'reference',
          index,
          counts.reportKeys,
          t
        ),
        isValue: true
      };
    }
    // A person's row of a report about several people (TGCLOUD-1868, Phase E4): their result, as the
    // report prints it, named for them by the caller where it can (whichIndividual.rowLabelOf).
    if (kind === 'individuals') {
      return {
        name: t('Person {{number}}’s result', { number: index + 1 }),
        isValue: false
      };
    }
    return { name: t('Not read'), isValue: false };
  }
  if (matches(text, 'geneList.stated')) {
    return { name: t('Number of genes stated'), isValue: true };
  }
  if (matches(text, 'geneList.withheld')) {
    return { name: t('Gene listed in the report'), isValue: false };
  }
  const top = text.split('.')[0];
  if (Object.prototype.hasOwnProperty.call(TOP_LEVEL, top)) {
    return { name: t(TOP_LEVEL[top]), isValue: true };
  }
  return { name: t('Value'), isValue: true };
}

/**
 * A box's label: "Variant, as read from the report". `anchor.label`, when the caller gives one,
 * names the field instead, as it is (the caller says it in the same language).
 */
export function ariaLabelOf(anchor, counts, t = english) {
  const path = anchor && anchor.path;
  const field = fieldOf(path, counts, t);
  const own =
    anchor && typeof anchor.label === 'string' && anchor.label.trim()
      ? anchor.label.trim()
      : null;
  const name = own || field.name;
  // Words the reader pointed at that are not a value it read: what it did not read, and the genes
  // it found but withheld from the Test (plan A5, B2).
  return field.isValue
    ? t('{{name}}, as read from the report', { name })
    : t('{{name}}, as printed in the report', { name });
}
